import {
  type ClipboardEvent,
  type KeyboardEvent,
  useCallback,
  useRef,
} from 'react';

import styles from './ui.module.css';

const EMPTY_SLOT = ' ';

/**
 * The 6-digit verification code entry, replacing Chakra's PinInput.
 *
 * Stays SIX REAL <input> elements: the Playwright e2e types into `nth(i)` and
 * the jest journeys query them individually, so a single masked field or a
 * contenteditable would break both. Behaviour matches the Chakra original —
 * type to advance, backspace on an empty box steps back, arrows move, and a
 * pasted code distributes across the boxes and fires `onComplete`.
 *
 * `value` is positional: a space marks an empty slot so the digits after a
 * cleared box stay where the user put them, and only trailing gaps are
 * trimmed. `onComplete` needs every slot filled, not just a six-character
 * string — a batched autofill writing the last box against stale state
 * produces `"     6"`, which is six characters of nothing.
 */
export function CodeInput({
  length,
  value,
  onChange,
  onComplete,
  disabled,
  ariaLabel,
}: {
  length: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  ariaLabel: (position: number, length: number) => string;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const focusAt = (index: number) => {
    refs.current[Math.min(Math.max(index, 0), length - 1)]?.focus();
  };

  // The digit in a slot, or '' when it's empty. Reading `value[index]` raw
  // hands back the EMPTY_SLOT space, which is truthy and fills the box up to
  // its maxLength of 1 — leaving a box that looks blank but rejects typing.
  const digitAt = (index: number) => {
    const char = value[index];
    return char === undefined || char === EMPTY_SLOT ? '' : char;
  };

  const commit = useCallback(
    (next: string) => {
      onChange(next);
      if (next.length === length && !next.includes(EMPTY_SLOT)) {
        onComplete?.(next);
      }
    },
    [length, onChange, onComplete]
  );

  const setDigits = (index: number, digits: string) => {
    const chars = value.padEnd(length, EMPTY_SLOT).split('');
    if (digits === '') {
      chars[index] = EMPTY_SLOT;
    } else {
      // Spread from `index` so a code pasted or autofilled into one box lands
      // across the following slots rather than into a single one.
      Array.from(digits)
        .slice(0, length - index)
        .forEach((digit, offset) => {
          chars[index + offset] = digit;
        });
    }
    return chars.join('').replace(/ +$/, '');
  };

  const handleChange = (index: number, raw: string) => {
    // A native autofill (or a fast paste landing in one box) can deliver the
    // whole code to a single field — spread it rather than truncating.
    const digits = raw.replace(/\s/g, '');
    const next = setDigits(index, digits);
    commit(next);
    if (digits) {
      focusAt(index + digits.length);
    }
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace' && !digitAt(index)) {
      event.preventDefault();
      if (index === 0) {
        // No slot to the left — `setDigits(-1, …)` would write a negative
        // index and re-commit an unchanged value.
        return;
      }
      commit(setDigits(index - 1, ''));
      focusAt(index - 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusAt(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (
    index: number,
    event: ClipboardEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\s/g, '');
    if (!pasted) {
      return;
    }
    const next = setDigits(index, pasted);
    commit(next);
    focusAt(index + pasted.length);
  };

  return (
    <div className={styles.codeRow}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          className={styles.codeField}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          aria-label={ariaLabel(index + 1, length)}
          value={digitAt(index)}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
        />
      ))}
    </div>
  );
}
